import { Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import GlobalSearchInput from '../components/Drawer/GlobalSearchInput';
import GlobalSearchResults from '../components/Drawer/GlobalSearchResults';
import { AnalyticsEvent, useAnalytics } from '../components/Providers/AnalyticsProvider';
import { getGlobalSearchResults } from '../redux/actions/globalSearchActions';
import { myFolder } from '../redux/consts';
import { setIsLoadingConversations } from '../redux/reducers/conversationReducer';
import { selectFolders, setCurrentFolder } from '../redux/reducers/folderReducer';
import {
  selectGlobalSearch,
  setAdvancedSearchSelected,
  setCreatedByMeOnly,
  setSearchQuery,
  setShowConversationsOnly,
} from '../redux/reducers/globalSearchReducer';
import { AppDispatch } from '../redux/store';
import { setFocusById } from '../services/util';
import { StyledChatContainer } from '../theme/CustomComponents';
import { Folder } from '../Types/folder';

const AdvancedSearch = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const analytics = useAnalytics();
  const { filter: filterFromURL } = useParams<{ filter?: string }>();
  const { advancedSearchSelected } = useSelector(selectGlobalSearch);
  const { currentFolder } = useSelector(selectFolders);

  // Skip initial cleanup in StrictMode so unmount logic only runs on real unmount
  const skipActualUnmount = useRef(false);

  // useEffect that runs on component unmount
  useEffect(() => {
    return () => {
      // In development StrictMode, effect mounts/unmounts twice; skip the first cleanup
      if (!skipActualUnmount.current) {
        skipActualUnmount.current = true;
        return;
      }

      dispatch(setShowConversationsOnly(undefined));
      dispatch(setSearchQuery(''));
      dispatch(setCreatedByMeOnly());
      dispatch(getGlobalSearchResults({ pageAction: 'reset' }));
    };
  }, [dispatch]);

  const onFolderCardClick = useCallback(
    (folder: Folder) => {
      analytics.event(
        AnalyticsEvent.WM_NIGEL_FOLDER_CLICK,
        {
          id: folder.id,
        },
        {
          folderId: folder.id,
        }
      );
      if (folder.id !== currentFolder?.id) {
        dispatch(setIsLoadingConversations(true));
        dispatch(setCurrentFolder(folder));
      }
      dispatch(setSearchQuery(''));
      navigate(`/folder/${folder.id}`);
    },
    [navigate, dispatch, analytics, currentFolder?.id]
  );

  const defaultHandleConversationUpdate = (
    conversationId?: string,
    folderId?: string,
    shouldFetchConversations = true
  ) => {
    analytics.event(
      AnalyticsEvent.WM_NIGEL_CONVERSATION_CLICK,
      {
        id: conversationId,
      },
      { conversationId }
    );
    if (shouldFetchConversations) {
      dispatch(setShowConversationsOnly(true));
      dispatch(getGlobalSearchResults({ pageAction: 'reset' }));
    }
    dispatch(setSearchQuery(''));

    const route = `/folder/${folderId ?? myFolder.id}`;

    if (conversationId) {
      navigate(`${route}/${conversationId}`);
    } else {
      navigate(route);
    }

    setFocusById('chat-input');
  };

  return (
    <div style={{ width: '100%' }}>
      <StyledChatContainer style={{ paddingRight: '0px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            flexDirection: 'column',
            paddingTop: '20px',
            gap: '20px',
            width: '100%',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <Typography variant="h5">{`${
              filterFromURL === 'chats' ? 'Recent Chats' : 'Folders'
            }`}</Typography>
          </div>
          <GlobalSearchInput
            showAdvancedFilters={true}
            searchSelected={advancedSearchSelected}
            setSearchSelected={setAdvancedSearchSelected}
          />
          <GlobalSearchResults
            onConversationUpdated={defaultHandleConversationUpdate}
            onFolderCardClick={onFolderCardClick}
            condensedView={false}
          />
        </div>
      </StyledChatContainer>
    </div>
  );
};

export default AdvancedSearch;
